import { useEffect, useState } from 'react';

export interface AppStoreInfo {
  id: string;
  trackName: string;
  bundleId: string;
  seller: string;
  genre: string;
  icon: string;
  screenshots: string[];
}

export type AppStoreMap = Record<string, AppStoreInfo>;

// mzstatic URLs end with a size segment (e.g. "392x696bb.jpg") that can be swapped for any size.
const resize = (url: string, size: string) => url.replace(/\/[^/]+$/, `/${size}`);

let cache: Promise<AppStoreMap> | null = null;

const fetchApps = (ids: string[]): Promise<AppStoreMap> => {
  if (!cache) {
    cache = fetch(`https://itunes.apple.com/lookup?id=${ids.join(',')}&country=tr&entity=software`)
      .then(response => response.json())
      .then(data => {
        const map: AppStoreMap = {};
        for (const app of data.results ?? []) {
          const id = String(app.trackId);
          map[id] = {
            id,
            trackName: app.trackName,
            bundleId: app.bundleId,
            seller: app.sellerName,
            genre: app.primaryGenreName,
            icon: resize(app.artworkUrl512 || app.artworkUrl100, '256x256bb.jpg'),
            screenshots: (app.screenshotUrls ?? []).map((url: string) => resize(url, '600x0w.jpg')),
          };
        }
        return map;
      })
      .catch(() => {
        cache = null;
        return {};
      });
  }
  return cache;
};

export const useAppStoreData = (ids: string[]) => {
  const [apps, setApps] = useState<AppStoreMap>({});
  const [loaded, setLoaded] = useState(false);
  const key = ids.join(',');

  useEffect(() => {
    let alive = true;
    fetchApps(key.split(',')).then(result => {
      if (!alive) return;
      setApps(result);
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, [key]);

  return { apps, loaded };
};
